// React
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Libraries
import PropTypes from "prop-types";
import { Device } from "mediasoup-client";
import { connect } from "react-redux";
import { Icon } from "semantic-ui-react";

// Redux
import {
	acceptIncomingCall,
	rejectIncomingCall,
	hangupIncomingCall,
	setCallMuteState,
	callCleared,
	transferIncomingCall,
} from "@Actions/calls";

// Components
import Text from "@Components/Text";
import CallTransferModal from "@Components/CallTransferModal";

// Assets
import { Ringtone } from "@Assets";

// Utils
import { getTimerLabel } from "@Utils/formats";

// Socket
import { socket } from "@Socket";

// Styles
import styles from "./styles.module.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const TERMINAL_STATUSES = new Set(["ended", "rejected", "missed"]);
const COMMAND_ACK_TIMEOUT_MS = 15000;
const ICE_GATHER_TIMEOUT_MS = 4000;
const ICE_SERVERS = [{ urls: "stun:stun.l.google.com:19302" }, { urls: "stun:stun1.l.google.com:19302" }];

// ─── WebRTC helpers ───────────────────────────────────────────────────────────

const waitForIceGathering = (pc) => {
	if (pc.iceGatheringState === "complete") return Promise.resolve();
	return new Promise((resolve) => {
		const timeout = window.setTimeout(resolve, ICE_GATHER_TIMEOUT_MS);
		const onStateChange = () => {
			if (pc.iceGatheringState === "complete") {
				window.clearTimeout(timeout);
				pc.removeEventListener("icegatheringstatechange", onStateChange);
				resolve();
			}
		};
		pc.addEventListener("icegatheringstatechange", onStateChange);
	});
};

const CALL_COMMAND_ACTION_BY_EVENT = {
	callAccept: "accept",
	callReject: "reject",
	callTerminate: "terminate",
};

// ─── Socket command helper ─────────────────────────────────────────────────────

const emitCallCommand = (event, payload) => {
	if (!socket || !socket.connected) {
		return Promise.reject(new Error("Socket is not connected"));
	}

	socket.emit(event, payload);
	if (event === "callAccept") socket.emit("acceptCall", payload);
	if (event === "callReject") socket.emit("rejectCall", payload);
	if (event === "callTerminate") socket.emit("terminateCall", payload);
	if (event === "callMute") socket.emit("muteCall", payload);
	if (event === "callUnmute") socket.emit("unmuteCall", payload);

	const expectedAction = CALL_COMMAND_ACTION_BY_EVENT[event];
	if (!expectedAction) return Promise.resolve(null);

	const expectedCallSid = String(payload?.callSid || "");

	return new Promise((resolve, reject) => {
		let timeoutId = null;

		const cleanup = () => {
			socket.off("callCommandAck", onAck);
			if (timeoutId) window.clearTimeout(timeoutId);
		};

		const onAck = (ack = {}) => {
			if (String(ack?.action || "") !== expectedAction) return;
			const ackCallSid = String(ack?.data?.callSid || "");
			if (expectedCallSid && ackCallSid && ackCallSid !== expectedCallSid) return;
			cleanup();
			if (ack?.success === false) {
				reject(new Error(ack?.message || "Call command failed"));
				return;
			}
			resolve(ack);
		};

		timeoutId = window.setTimeout(() => {
			cleanup();
			reject(new Error("Call command timed out"));
		}, COMMAND_ACK_TIMEOUT_MS);

		socket.on("callCommandAck", onAck);
	});
};

const emitMediasoupCommand = (event, payload) => {
	if (!socket || !socket.connected) return Promise.reject(new Error("Socket is not connected"));
	return new Promise((resolve, reject) => {
		const timeoutId = window.setTimeout(() => {
			return reject(new Error("Media signaling timed out"));
		}, COMMAND_ACK_TIMEOUT_MS);
		socket.emit(event, payload, (response = {}) => {
			window.clearTimeout(timeoutId);
			if (!response.success) {
				reject(new Error(response.message || "Media signaling failed"));
				return;
			}
			resolve(response.data);
		});
	});
};

// ─── Status label ─────────────────────────────────────────────────────────────

const getStatusLabel = (status) => {
	const s = String(status || "").toLowerCase();
	if (s === "incoming") return "Llamada Entrante";
	if (s === "ongoing") return "En Curso";
	if (s === "rejected") return "Rechazada";
	if (s === "missed") return "Sin Respuesta";
	if (s === "ended") return "Finalizada";
	return "Llamada";
};

// ─── Component ────────────────────────────────────────────────────────────────

const CallWidget = ({ dispatch, activeCall, commandInProgress }) => {
	const widgetRef = useRef(null);
	const posRef = useRef({ x: 0, y: 0 });
	const [pos, setPos] = useState(null); // null = use CSS default (right/top)
	const [collapsed, setCollapsed] = useState(false);
	const [localElapsed, setLocalElapsed] = useState(0);
	const [transferOpen, setTransferOpen] = useState(false);

	const isDragging = useRef(false);
	const dragStart = useRef({ mouseX: 0, mouseY: 0, posX: 0, posY: 0 });
	const ringtoneRef = useRef(null);

	// WebRTC
	const peerConnectionRef = useRef(null);
	const mediaDeviceRef = useRef(null);
	const sendTransportRef = useRef(null);
	const recvTransportRef = useRef(null);
	const mediaProducerRef = useRef(null);
	const mediaConsumerRef = useRef(null);
	const localStreamRef = useRef(null);
	const remoteAudioRef = useRef(null);
	const setupTokenRef = useRef(0); // incremented by cleanup to cancel in-flight setup

	const status = String(activeCall?.status || "").toLowerCase();
	const isSipCall = String(activeCall?.callMode || activeCall?.session?.mode || "p2p").toLowerCase() === "sip";
	const callSid = String(activeCall?.callSid || "");
	const isRinging = status === "incoming";
	const isInCall = status === "ongoing";
	const isTerminal = TERMINAL_STATUSES.has(status);
	const isActiveCall = useMemo(() => {
		if (!activeCall) return false;
		return !TERMINAL_STATUSES.has(String(activeCall.status || "").toLowerCase());
	}, [activeCall]);

	// ── Ringtone ──────────────────────────────────────────────────────────────

	useEffect(() => {
		ringtoneRef.current = new Audio(Ringtone);
		ringtoneRef.current.loop = true;
		ringtoneRef.current.preload = "auto";
		return () => {
			if (ringtoneRef.current) {
				ringtoneRef.current.pause();
				ringtoneRef.current = null;
			}
		};
	}, []);

	useEffect(() => {
		if (!ringtoneRef.current) return;
		if (isRinging) {
			ringtoneRef.current.currentTime = 0;
			ringtoneRef.current.play().catch(() => {});
		} else {
			ringtoneRef.current.pause();
			ringtoneRef.current.currentTime = 0;
		}
	}, [isRinging]);

	// ── Duration timer ────────────────────────────────────────────────────────

	useEffect(() => {
		if (!isInCall) {
			setLocalElapsed(0);
			return;
		}
		const connectedAt = Number(activeCall?.connectedAt || activeCall?.startedAt || Date.now());
		const id = window.setInterval(() => {
			setLocalElapsed(Math.max(0, Math.floor((Date.now() - connectedAt) / 1000)));
		}, 1000);

		return () => {
			return window.clearInterval(id);
		};
	}, [isInCall, activeCall?.connectedAt, activeCall?.startedAt]);

	// ── Auto-clear terminal calls ─────────────────────────────────────────────

	useEffect(() => {
		if (activeCall && isTerminal) {
			const id = window.setTimeout(() => {
				return dispatch(callCleared());
			}, 2500);
			return () => {
				return window.clearTimeout(id);
			};
		}
	}, [dispatch, activeCall, isTerminal]);

	// ── WebRTC ────────────────────────────────────────────────────────────────

	const cleanupWebRTC = useCallback(() => {
		// Bump token so any in-flight setupWebRTC aborts after its next await
		setupTokenRef.current += 1;

		if (localStreamRef.current) {
			localStreamRef.current.getTracks().forEach((t) => {
				t.stop();
			});
			localStreamRef.current = null;
		}
		if (peerConnectionRef.current) {
			peerConnectionRef.current.ontrack = null;
			peerConnectionRef.current.close();
			peerConnectionRef.current = null;
		}
		if (mediaConsumerRef.current) mediaConsumerRef.current.close();
		if (mediaProducerRef.current) mediaProducerRef.current.close();
		if (recvTransportRef.current) recvTransportRef.current.close();
		if (sendTransportRef.current) sendTransportRef.current.close();
		mediaConsumerRef.current = null;
		mediaProducerRef.current = null;
		recvTransportRef.current = null;
		sendTransportRef.current = null;
		mediaDeviceRef.current = null;
		if (remoteAudioRef.current) {
			remoteAudioRef.current.srcObject = null;
		}
	}, []);

	const setupWebRTC = useCallback(
		async (offerSession) => {
			cleanupWebRTC();
			const token = setupTokenRef.current;
			const checkCancelled = () => {
				if (setupTokenRef.current !== token) throw new Error("WebRTC setup cancelled");
			};

			const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
			peerConnectionRef.current = pc;

			pc.ontrack = (event) => {
				if (remoteAudioRef.current && event.streams?.[0]) {
					const [firstStream] = event.streams;
					remoteAudioRef.current.srcObject = firstStream;
					remoteAudioRef.current.play().catch(() => {
						/* autoplay blocked */
					});
				}
			};

			try {
				const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
				checkCancelled();
				localStreamRef.current = stream;
				stream.getTracks().forEach((track) => {
					pc.addTrack(track, stream);
				});
			} catch (mediaErr) {
				if (mediaErr.message === "WebRTC setup cancelled") throw mediaErr;
				// eslint-disable-next-line no-console
				console.warn("Microphone unavailable:", mediaErr?.message);
			}

			checkCancelled();

			const remoteSdpType = offerSession?.sdp_type;
			const remoteSdp = offerSession?.sdp;

			if (remoteSdpType === "offer" && remoteSdp) {
				await pc.setRemoteDescription({ type: "offer", sdp: remoteSdp });
				checkCancelled();
				const answer = await pc.createAnswer();
				await pc.setLocalDescription(answer);
				await waitForIceGathering(pc);
				checkCancelled();
				return { sdp_type: "answer", sdp: pc.localDescription.sdp };
			}

			const offer = await pc.createOffer({ offerToReceiveAudio: true });
			await pc.setLocalDescription(offer);
			await waitForIceGathering(pc);
			checkCancelled();
			return { sdp_type: "offer", sdp: pc.localDescription.sdp };
		},
		[cleanupWebRTC]
	);

	const setupMediasoup = useCallback(async () => {
		cleanupWebRTC();
		const token = setupTokenRef.current;
		const ensureActive = () => {
			if (setupTokenRef.current !== token) throw new Error("Mediasoup setup cancelled");
		};
		const command = (event, data = {}) => {
			return emitMediasoupCommand(event, { callSid, channelId: activeCall?.channelId, ...data });
		};

		const capabilitiesResponse = await command("mediasoup:getRouterRtpCapabilities");
		ensureActive();
		const device = new Device();
		await device.load({ routerRtpCapabilities: capabilitiesResponse.rtpCapabilities });
		mediaDeviceRef.current = device;

		const sendParams = await command("mediasoup:createTransport");
		ensureActive();
		const sendTransport = device.createSendTransport(sendParams);
		sendTransportRef.current = sendTransport;
		sendTransport.on("connect", ({ dtlsParameters }, callback, errback) => {
			command("mediasoup:connectTransport", { transportId: sendTransport.id, dtlsParameters }).then(callback).catch(errback);
		});
		sendTransport.on("produce", ({ kind, rtpParameters }, callback, errback) => {
			command("mediasoup:produce", { transportId: sendTransport.id, kind, rtpParameters })
				.then(({ id }) => {
					return callback({ id });
				})
				.catch(errback);
		});

		const recvParams = await command("mediasoup:createTransport");
		ensureActive();
		const recvTransport = device.createRecvTransport(recvParams);
		recvTransportRef.current = recvTransport;
		recvTransport.on("connect", ({ dtlsParameters }, callback, errback) => {
			command("mediasoup:connectTransport", { transportId: recvTransport.id, dtlsParameters }).then(callback).catch(errback);
		});

		const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
		ensureActive();
		localStreamRef.current = stream;
		const [audioTrack] = stream.getAudioTracks();
		mediaProducerRef.current = await sendTransport.produce({ track: audioTrack });

		const consumerParams = await command("mediasoup:consume", {
			transportId: recvTransport.id,
			rtpCapabilities: device.rtpCapabilities,
		});
		ensureActive();
		const consumer = await recvTransport.consume(consumerParams);
		mediaConsumerRef.current = consumer;
		if (remoteAudioRef.current) {
			remoteAudioRef.current.srcObject = new MediaStream([consumer.track]);
			await remoteAudioRef.current.play().catch(() => {});
		}
		await command("mediasoup:resumeConsumer", { consumerId: consumer.id });
	}, [activeCall?.channelId, callSid, cleanupWebRTC]);

	// Tear down WebRTC when call reaches a terminal status
	useEffect(() => {
		if (isTerminal) cleanupWebRTC();
	}, [isTerminal, cleanupWebRTC]);

	// Safety net: clean up whenever activeCall is cleared (covers all hangup paths)
	useEffect(() => {
		if (!activeCall) cleanupWebRTC();
	}, [activeCall, cleanupWebRTC]);

	// Final safety net on component unmount
	useEffect(() => {
		return () => {
			cleanupWebRTC();
		};
	}, [cleanupWebRTC]);

	// ── Expand on new call ────────────────────────────────────────────────────

	useEffect(() => {
		if (activeCall && isActiveCall) {
			setCollapsed(false);
		}
	}, [activeCall?.callSid]);

	// ── Drag ──────────────────────────────────────────────────────────────────

	const onMouseDown = useCallback(
		(e) => {
			if (e.button !== 0) return;
			isDragging.current = true;
			// On first drag, seed the position from the widget's current rendered location
			const rect = widgetRef.current?.getBoundingClientRect();
			if (pos === null && rect) {
				posRef.current = { x: rect.left, y: rect.top };
			}
			dragStart.current = {
				mouseX: e.clientX,
				mouseY: e.clientY,
				posX: posRef.current.x,
				posY: posRef.current.y,
			};
			e.preventDefault();
		},
		[pos]
	);

	const onMouseMove = useCallback((e) => {
		if (!isDragging.current) return;
		const widgetWidth = widgetRef.current?.offsetWidth || 320;
		const dx = e.clientX - dragStart.current.mouseX;
		const dy = e.clientY - dragStart.current.mouseY;
		const next = {
			x: Math.max(0, Math.min(window.innerWidth - widgetWidth, dragStart.current.posX + dx)),
			y: Math.max(0, Math.min(window.innerHeight - 64, dragStart.current.posY + dy)),
		};
		posRef.current = next;
		setPos(next);
	}, []);

	const onMouseUp = useCallback(() => {
		isDragging.current = false;
	}, []);

	useEffect(() => {
		document.addEventListener("mousemove", onMouseMove);
		document.addEventListener("mouseup", onMouseUp);
		return () => {
			document.removeEventListener("mousemove", onMouseMove);
			document.removeEventListener("mouseup", onMouseUp);
		};
	}, [onMouseMove, onMouseUp]);

	// ── Call actions ──────────────────────────────────────────────────────────

	const handleAccept = useCallback(async () => {
		if (!callSid) return;
		let session = null;
		try {
			if (!isSipCall) session = await setupWebRTC(activeCall?.session);
		} catch (rtcErr) {
			// eslint-disable-next-line no-console
			console.warn("WebRTC setup failed, accepting without session:", rtcErr?.message);
		}
		const accepted = await dispatch(
			acceptIncomingCall({
				callSid,
				channelId: activeCall?.channelId,
				session,
				emitCommand: emitCallCommand,
			})
		);
		if (!accepted) {
			cleanupWebRTC();
			return;
		}
		if (isSipCall) {
			try {
				await setupMediasoup();
			} catch (mediaError) {
				cleanupWebRTC();
				// eslint-disable-next-line no-console
				console.warn("Mediasoup setup failed:", mediaError?.message);
				await dispatch(
					hangupIncomingCall({
						callSid,
						channelId: activeCall?.channelId,
						emitCommand: emitCallCommand,
					})
				);
			}
		}
	}, [dispatch, callSid, activeCall?.channelId, activeCall?.session, isSipCall, setupMediasoup, setupWebRTC, cleanupWebRTC]);

	const handleReject = useCallback(() => {
		if (!callSid) return;
		dispatch(
			rejectIncomingCall({
				callSid,
				channelId: activeCall?.channelId,
				emitCommand: emitCallCommand,
			})
		);
	}, [dispatch, callSid, activeCall?.channelId]);

	const handleHangup = useCallback(() => {
		if (!callSid) return;
		dispatch(
			hangupIncomingCall({
				callSid,
				channelId: activeCall?.channelId,
				emitCommand: emitCallCommand,
			})
		);
	}, [dispatch, callSid, activeCall?.channelId]);

	const handleTransfer = useCallback(
		async ({ userId, groupId }) => {
			const success = await dispatch(transferIncomingCall({ callSid, userId, groupId }));
			if (success) cleanupWebRTC();
			return success;
		},
		[callSid, cleanupWebRTC, dispatch]
	);

	const handleToggleMute = useCallback(async () => {
		if (!callSid) return;
		const nextMuted = !activeCall?.micMuted;
		dispatch(setCallMuteState(nextMuted));

		const mediaProducer = mediaProducerRef.current;
		if (mediaProducer) {
			if (nextMuted) {
				mediaProducer.pause();
				if (mediaProducer.track) mediaProducer.track.stop();
			} else {
				try {
					const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
					if (localStreamRef.current) {
						localStreamRef.current.getTracks().forEach((track) => {
							return track.stop();
						});
					}
					localStreamRef.current = stream;
					const [audioTrack] = stream.getAudioTracks();
					await mediaProducer.replaceTrack({ track: audioTrack });
					mediaProducer.resume();
				} catch (error) {
					// eslint-disable-next-line no-console
					console.warn("Failed to restore mediasoup microphone:", error?.message);
				}
			}
		}

		const pc = peerConnectionRef.current;
		if (pc) {
			const audioSenders = pc.getSenders().filter((s) => {
				return s.track?.kind === "audio" || s.track === null;
			});
			if (nextMuted) {
				// Stop the track entirely so Chrome releases the mic indicator
				for (const sender of audioSenders) {
					if (sender.track) sender.track.stop();
					try {
						await sender.replaceTrack(null);
					} catch (_) {
						// ignore
					}
				}
				if (localStreamRef.current) {
					localStreamRef.current.getTracks().forEach((t) => {
						t.stop();
					});
					localStreamRef.current = null;
				}
			} else {
				// Get a fresh mic stream and restore it into the sender
				try {
					const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
					localStreamRef.current = stream;
					const [audioTrack] = stream.getAudioTracks();
					const target = audioSenders[0];
					if (target) await target.replaceTrack(audioTrack);
				} catch (e) {
					// eslint-disable-next-line no-console
					console.warn("Failed to restore mic:", e?.message);
				}
			}
		}

		emitCallCommand(nextMuted ? "callMute" : "callUnmute", {
			callSid,
			channelId: activeCall?.channelId,
			muted: nextMuted,
		});
	}, [dispatch, callSid, activeCall?.channelId, activeCall?.micMuted]);

	// ── Render ────────────────────────────────────────────────────────────────

	if (!activeCall) return null;

	const contact = activeCall?.raw?.chat?.contact;
	const callerName = activeCall.callerName || contact?.name || activeCall.from || "Número desconocido";
	const callerPhone = activeCall.from || contact?.phone || null;
	const showPhone = callerPhone && callerPhone !== callerName;
	const durationLabel = getTimerLabel(Number(activeCall.duration || 0) + localElapsed);
	const isMuted = Boolean(activeCall.micMuted);

	const widgetStyle = pos !== null ? { left: pos.x, top: pos.y } : {};

	return (
		<div ref={widgetRef} className={`${styles.widget} ${isRinging ? styles.widgetRinging : ""}`} style={widgetStyle}>
			{/* eslint-disable-next-line react/no-unknown-property */}
			<audio ref={remoteAudioRef} autoPlay playsInline style={{ display: "none" }} />
			<div className={styles.header} role="button" tabIndex={0} onMouseDown={onMouseDown}>
				<div className={styles.headerLeft}>
					<div className={`${styles.phoneCircle} ${isRinging ? styles.pulse : ""}`}>
						<Icon name="phone" className={styles.phoneIcon} />
					</div>
					<div className={styles.headerInfo}>
						<Text className={styles.callerName}>{callerName}</Text>
						{showPhone && <Text className={styles.callerPhone}>{callerPhone}</Text>}
						<Text className={styles.statusLabel}>{getStatusLabel(activeCall.status)}</Text>
					</div>
				</div>

				<div className={styles.headerRight}>
					{isInCall && <Text className={styles.duration}>{durationLabel}</Text>}
					<button
						type="button"
						className={styles.collapseBtn}
						onClick={(e) => {
							e.stopPropagation();
							setCollapsed((c) => {
								return !c;
							});
						}}>
						<Icon name={collapsed ? "chevron up" : "chevron down"} className={styles.collapseIcon} />
					</button>
				</div>
			</div>

			{/* Body */}
			{!collapsed && (
				<div className={styles.body}>
					{isRinging && (
						<div className={styles.actionRow}>
							<button className={styles.acceptBtn} type="button" disabled={commandInProgress === "accept"} onClick={handleAccept}>
								<Icon name="phone" />
								Aceptar
							</button>
							<button
								className={styles.rejectBtn}
								type="button"
								disabled={commandInProgress === "reject" || isTerminal}
								onClick={handleReject}>
								<Icon name="phone" />
								Rechazar
							</button>
						</div>
					)}

					{isInCall && (
						<div className={styles.inCallRow}>
							<button className={`${styles.muteBtn} ${isMuted ? styles.mutedActive : ""}`} type="button" onClick={handleToggleMute}>
								<Icon name={isMuted ? "microphone slash" : "microphone"} />
								{isMuted ? "Activar mic" : "Silenciar"}
							</button>
							{isSipCall && (
								<button
									className={styles.transferBtn}
									type="button"
									onClick={() => {
										return setTransferOpen(true);
									}}>
									<Icon name="exchange" />
									Transferir
								</button>
							)}
							<button className={styles.hangupBtn} type="button" disabled={commandInProgress === "terminate"} onClick={handleHangup}>
								<Icon name="phone" />
								Terminar
							</button>
						</div>
					)}

					{isTerminal && <Text className={styles.terminalMsg}>Llamada finalizada</Text>}
				</div>
			)}
			<CallTransferModal
				dispatch={dispatch}
				open={transferOpen}
				onClose={() => {
					return setTransferOpen(false);
				}}
				onTransfer={handleTransfer}
			/>
		</div>
	);
};

CallWidget.propTypes = {
	dispatch: PropTypes.func,
	activeCall: PropTypes.object,
	commandInProgress: PropTypes.string,
};

function mapStateToProps({ calls }) {
	return {
		activeCall: calls.activeCall,
		commandInProgress: calls.commandInProgress,
	};
}

export default connect(mapStateToProps)(CallWidget);
